import { NavLink, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown, LogOut, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { resolveDisplayName } from '@/lib/display-name';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { navItems, accountItems } from './navItems';

/**
 * The `lg`-and-up navigation. Below that width `AppHeader`'s Sheet carries the
 * same list from the same source.
 *
 * The connection dot moved to `AppHeader` in Phase 15 and must not come back
 * here: exactly one element in the tree may carry `data-testid="connection-dot"`,
 * and the header is the only shell surface that renders at every viewport.
 * See the comment on `AppHeader` for why that matters to `ws-live-smoke.spec.ts`.
 *
 * ── The identity line is a menu, and the nav is the flow ───────────────────
 *
 * The bottom-left used to be three stacked things: an email address that did
 * nothing, and a sign-out button, with Settings filed nine items up among the
 * surfaces of the product itself. That put account plumbing inside the flow and
 * left the one line identifying the signed-in person inert.
 *
 * Now it is one control (operator, 2026-08-16: "maybe we bring this as part of
 * a user menu on the bottom-left email. The sidebar should be reserved for the
 * user flow"). The name she chose is the trigger; Settings and Sign out are
 * inside it. `navItems` is left holding only the surfaces someone moves through
 * while becoming somebody, which is what a nav is for.
 */
export function AppSidebar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  return (
    <aside className="hidden lg:flex flex-col w-64 bg-card border-r border-border">
      {/* Brand */}
      <div className="flex items-center gap-2 px-6 py-5 border-b border-border">
        <Sparkles className="size-6 text-primary" />
        <span className="font-display text-lg text-foreground">becoming.you</span>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
              )
            }
          >
            <item.icon size={18} />
            {t(item.i18nKey)}
          </NavLink>
        ))}
      </nav>

      {/* Identity + account menu */}
      {user && (
        <div className="px-3 py-4 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger
              className="flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              data-testid="user-menu-trigger"
            >
              <span className="truncate">{resolveDisplayName(user)}</span>
              <ChevronsUpDown size={16} className="ml-auto shrink-0 opacity-60" />
            </DropdownMenuTrigger>
            {/* `side="top"` because the trigger is pinned to the bottom of the
                viewport: the default would open the menu off-screen. */}
            <DropdownMenuContent side="top" align="start" className="w-[13.5rem]">
              {accountItems.map((item) => (
                <DropdownMenuItem key={item.to} asChild>
                  <Link to={item.to} className="flex items-center gap-3">
                    <item.icon size={16} />
                    {t(item.i18nKey)}
                  </Link>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => void handleLogout()}>
                <LogOut size={16} className="mr-3" />
                {t('app.sidebar.sign_out')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </aside>
  );
}
